import ScriptingEffect from '../scripting/ScriptingEffect.js';
import Figure from "effects/midi/jaki/Figure.js"
import NodeMessaging from "messaging/NodeMessaging.js";
import {TIDALS, CYCLE_RESTART, SCALE, TRIGGER_ROUTING} from "messaging/message-types.js";
import {t_truncate} from '../scripting/tlist.js';
import {vary_every} from '../scripting/tevery.js';
import {t_simplify} from '../scripting/tgenerate.js';
import {add, multiply} from '../scripting/lazy.js';
import {scl, t_put_scale} from '../scripting/t_harmony.js';
import {eq, and} from '../scripting/compare.js';
import {apply_rules, jaki_accent} from '../scripting/jaki.js';
import {sublist, len, lconcat, concat} from '../scripting/list.js';
import {shift} from '../scripting/beat.js';
import {t_trigger} from '../scripting/triggerAttackRelease.js';
import {rules, when, ACCENT, REST} from '../scripting/trules.js';
import {BOTH, LEFT, RIGHT, NONE} from './constants.js';
import JakiParams from './JakiParams.js';
import RulesRack from './RulesRack.js';
import {pipe} from '../scripting/pipe.js';
import {
    filter,
    s,
    switchOutput,
    execute,
    rolling,
    notes,
    steps,
    prob,
    cond,
    p,
    lazy
} from '../scripting/lang.js';
import {hand, t_print, t_cond, j_tidal, tparse} from '../scripting/tidal.js';
import {swing, offset} from '../scripting/swing.js';
import {parse, getCycleString} from 'effects/midi/tidal/parser.js';
import {fig} from '../scripting/t_figure.js';
import Figures from './Figures.js';

class JakiEffect extends NodeMessaging(ScriptingEffect) {
    constructor(track, zObject) {
        super("Jaki", track);
        this.zObject = zObject;
        this.rules = [
            new RulesRack(track, this),
            new RulesRack(track, this),
            new RulesRack(track, this),
            new RulesRack(track, this),
        ];

        this.scaleRules = [
            [],
            [],
            [],
            [],
            [],
            []
        ];

        // preemptively "use tidals" so they initialize
        for (let i=0; i < 6; i++) {
            this.useTidal();
        }
        this.freeTidals();

        this.jakiParams = new JakiParams(this.track.subSequence);
        this.figures = new Figures(this.track.subSequence);

        this.clear();

        this.numberOfOutputs = 1;

        this.newOutlet(SCALE);
        this.newOutlet(CYCLE_RESTART);
        this.newOutlet(TIDALS);
        this.newOutlet("figure-change");
    }

    clear() {
        if (this.tidals) {
            this.tidals.forEach(
                t => t.clear());
        }
        this.freeTidals();
        
        let p1 = lconcat(() => this.figures._figures);
        p1 = shift(p1, () => this.jakiParams.shift1Parameter.get());
        let p1_ruled = jaki_accent(
            p1,
            0,
            () => this.rules[0].rules
        );

        console.log('pl_ruled', p1_ruled);
        
        let emptyRules = [];
        let shift_rules = (pattern, shiftParam, ruleParam) =>
            t_cond(and(() => Math.round(shiftParam()) === 0, () => ruleParam() === null),
                 pattern,
                 jaki_accent(
                     shift(pattern, () => Math.round(shiftParam())),
                     () => 0,
                     () => ruleParam() === null ? emptyRules : this.rules[Math.round(ruleParam()) % this.rules.length].rules
                 ));

        let _scale1 = scl(p1);
        let _scale2 = [0];

        let scale = (pattern, param, scaleRules, debug) => {
            return t_cond(eq(param, true),
                   t_put_scale(pattern, _scale1, scaleRules, debug),
                   t_put_scale(pattern, _scale2, undefined, debug));
        };

       const j_trigger = (pattern, number) =>
              t_trigger(
                  scale(
                      shift_rules(
                          t_truncate(pattern, this.getParam("truncate", number, Math.round)),
                          this.getParam("shift", number, Math.round),
                          this.getParam("rule", number, Math.round),
                      ),
                      this.getParam("scale", number),
                      () => this.scaleRules[number - 1]
                  ),
                  () => this.getTrackToRoute(number),
                  (p) => s(
                      j_tidal(
                          p,
                          this.getParam(
                              "tempo",
                              number,
                              (x) => 0.25*Math.pow(2, Math.round(x) )),
                          this.getParam(
                              "triplet", number)),
                      this.swing(number),
                      this.offset(number),
                      this.handleHand(number),
                  ),
                  this.zObject
              );

       const j_trigger_no = (pattern, number) =>
              t_trigger(
                  scale(
                      pattern,
                      this.getParam("scale", number),
                      () => this.scaleRules[number - 1])
                  ,
                  () => this.getTrackToRoute(number),
                  (p) => s(
                      j_tidal(
                          p,

                          this.getParam(
                              "tempo",
                              number,
                              (x) => 0.25*Math.pow(2, x)),
                          this.getParam(
                              "triplet", number)
                      ),
                      this.swing(number),
                      this.offset(number),
                      this.handleHand(number)
                  ),
                  this.zObject
              );


        this.myScript = pipe(
            p(
                () => {
                    // this section of the program is meant to
                    // check if the scale has changed and share it
                    // with whatever nodes are listening to this
                    let _scale = _scale1();
                    if (_scale !== this._scale) {
                        // scale changed so publish a message
                        this._scale = _scale;
                        this.sendMessage(SCALE, _scale);
                    }
                    this.currentScale = _scale1();
                    this.sendMessage(TIDALS, this.tidals);
                    return filter(false);
                },
                j_trigger_no(
                    t_truncate(scale(
                        p1_ruled,
                        this.scale1Parameter,
                        () => this.scaleRules[0],
                        true),
                               this.getParam("truncate", 1, Math.round)),
                    1),
                j_trigger(p1_ruled, 2),
                j_trigger(p1_ruled, 3),
                j_trigger(p1_ruled, 4),
                j_trigger(p1_ruled, 5),
                j_trigger(p1_ruled, 6),
            ),
        );
    }

    getTrackToRoute(n) {
        return n;
        
        // check if were connected to anything?
        let inlets = this.getConnectedOutlets(TRIGGER_ROUTING);

        let tracks = [];
        let trackNumber = n;
        let pads = this.track.subSequence.pads;

        for (let connection of inlets) {
            // look for a pad that is here
            let pad = connection.destNode;
            if (pad) {
                // return the track number of the nth
                if (pad.tracks[n - 1]) {
                    tracks.push(pad.tracks[n-1].trackNumber+1);
                }
            }
        }

        if (inlets.length > 0) {
            return tracks;
        }
        return n;
    }

    getParam(name, number, fn=(x) => x) {
        return () => fn(this.jakiParams[name + number + "Parameter"].get());
    }

    pName(name, number) {
        return this.jakiParams[name + number + "Parameter"];
    }

    swing(number) {
        let _swing = () => this.pName("swing", number).get();
        return swing(_swing);
    }

    offset(number) {
        let _offset = () => this.pName("offset", number).get();
        return offset(_offset);
    }

    handleHand(number) {
        let _hand = () => this.pName("hand", number).get();
        return hand(_hand);
    }

    get script() {
        return this.myScript;
    }

    getCopy(track, ignoreId=true) {
        let fx = new JakiEffect(track);
        fx.fromJson(this.getJson(), ignoreId);
        return fx;
    }

    stop() {
        super.stop();
        this.freeTidals();
    }

    getJson() {
        return {
            ... super.getJson(),
            jakiParams: this.jakiParams.getJson(),
            figures: this.figures.getJson(),
            rules: this.rules.map(x => x.getJson()),
            scaleRules: this.scaleRules
        };
    }
    registerContext(context) {
        context = context.forEffect(this);
        this.getParameters().forEach(
            parameter =>
                parameter.setContext(context));
    }

    getParameters() {
        let x = [
            ... this.parameters,
            ... this.jakiParams.parameters,
            ... this.figures.figures.flatMap(
                f => f.parameters),
            ... this.rules.flatMap(
                r => r.getParameters())];
        return x.filter(y => y);
    }

    fromJson(json) {
        let bulk = super.fromJson(json);

        if (json.jakiParams) {
            this.jakiParams.fromJson(json.jakiParams);
        } else {
            this.jakiParams.fromJson(json);
        }

        if (json.figures) {
            if (Array.isArray(json.figures)) {
                this.figures.fromJson({
                    id: this.figures.id,
                    figures: json.figures
                });
            } else {
                this.figures.fromJson(json.figures);
            }
       }

        if (json.rules) {
            this.rules.forEach(
                (x,i) => {
                    if (json.rules[i]) {
                        x.effects = [];
                        x.fromJson(json.rules[i]);
                    }
                });
        }

        if (json.scaleRules) {
            this.scaleRules = json.scaleRules;
        }
        return bulk;
    }
}


export default JakiEffect;
